<template>
    <b-card>
        <b-card-header>
            <b-row class="mt-2 flex-nowrap">
                <b-col lg="8" xl="9" class="search-field">
                    <div class="input-group">
                        <input
                            class="form-control"
                            type="text"
                            :placeholder="$translate('search_tasks')"
                            v-model="query"
                        />
                        <div class="input-group-prepend bg-transparent">
                            <i class="mdi mdi-magnify"></i>
                        </div>
                    </div>
                </b-col>

                <b-col lg="4" xl="3" class="mt-1 p-1">
                    <b-button
                        class="me-2"
                        variant="blue-button"
                        @click="toggle_filters = !toggle_filters"
                    >
                        <span style="white-space: nowrap">
                            {{ $translate('filters') }}
                            <b-icon
                                :icon="`chevron-${
                                    toggle_filters ? 'up' : 'down'
                                }`"
                            ></b-icon>
                        </span>
                    </b-button>

                    <b-button
                        variant="blue-button"
                        @click="resetFilters"
                        v-b-tooltip.hover
                        :title="$translate('reset_filters')"
                    >
                        <b-icon icon="arrow-repeat" variant="dark"></b-icon>
                    </b-button>
                </b-col>

                <b-col class="text-right">
                    <slot></slot>
                </b-col>
            </b-row>

            <schedule-query-builder
                v-show="toggle_filters"
                v-model="filters"
                :user="user"
                :officers="officers"
                :roles="roles"
            />
        </b-card-header>
        <div class="m-2 font-weight-bold">{{ selectedMonthEventsText }}</div>
        <FullCalendar
            :options="calendarOptions"
            ref="calendar"
            class="full-calendar w-100 h-100"
        />
    </b-card>
</template>

<script>
import _ from 'lodash'
import ScheduleQueryBuilder from './ScheduleQueryBuilder.vue'
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import listPlugin from '@fullcalendar/list'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import moment from 'moment'
import esLocale from '@fullcalendar/core/locales/es'
import schedulerMixins from '../../mixins/schedulerMixins'

export default {
    components: {
        FullCalendar,
        ScheduleQueryBuilder,
    },
    props: ['crm', 'roles', 'user', 'officers', 'crmSettingValue'],
    mixins: [schedulerMixins],

    data() {
        return {
            query: '',
            loaded: true,
            calendarOptions: {
                plugins: [
                    dayGridPlugin,
                    listPlugin,
                    timeGridPlugin,
                    interactionPlugin,
                ],
                headerToolbar: {
                    left: '',
                    center: 'prev today next',
                    right: 'dayGridMonth dayGridWeek dayGridDay listDay',
                },
                buttonText: {
                    today: 'This Month',
                },
                datesSet: this.updateCustomButton,
                initialView: 'dayGridMonth',
                events: [],
                eventContent: this.renderEventContent,
                selectable: true,
                selectMirror: true,
                dayMaxEvents: true,
                weekends: true,
                locale:
                    this.getLocale(localStorage.getItem('language')) || 'en',
            },
            selectedMonthEventsText: '',
            toggle_filters: false,
            filters: {
                customer_id: null,
                type_id: null,
                default_engineer_id: this.user.id,
                dashboard: 1,
                role_id: null,
                next_service_from: null,
                next_service_to: null,
                last_service_from: null,
                last_service_to: null,
                warranty_from: null,
                warranty_to: null,
            },
        }
    },
    computed: {
        assets() {
            return this.$store.state.assets.assets
        },
    },

    mounted() {
        const calendarApi = this.$refs.calendar.getApi()
        calendarApi.on('eventClick', this.handleEventClick)
        this.doSearch()
        this.updateCustomButton()
    },

    watch: {
        filters: {
            handler: function () {
                this.doSearch()
            },
            deep: true,
        },
        query: function (query) {
            this.$router.push({ query: { query } })
            this.doSearch()
        },
        assets: {
            handler: function (val) {
                if (val) this.loaded = true
            },
            deep: true,
        },
    },
    created() {},
    methods: {
        getLocale(language) {
            if (language == 'es') {
                return esLocale
            }
        },
        resetFilters() {
            this.filters = {
                customer_id: null,
                type_id: null,
                default_engineer_id: this.user.id,
                role_id: null,
                next_service_from: null,
                next_service_to: null,
                last_service_from: null,
                last_service_to: null,
                warranty_from: null,
                warranty_to: null,
            }
            this.doSearch()
        },
        handleEventClick(eventClickInfo) {
            const type_id = this.crmSettingValue.type_id
            const eventTypeId = eventClickInfo.event.extendedProps.type_id
            const eventId = eventClickInfo.event.extendedProps.eventId
            if (eventTypeId == type_id) {
                this.$router.push(`/ams/crm/${eventId}`)
            } else {
                this.$router.push(`/ams/assets/${eventId}`)
            }
        },
        doSearch: _.debounce(async function () {
            await this.pureSearch()
            this.updateCustomButton()
        }, 500),

        renderEventContent(eventInfo) {
            const type_id = this.crmSettingValue.type_id
            const customerName = eventInfo.event.extendedProps.customerName
            const agentName = eventInfo.event.extendedProps.agentName
            const typeName = eventInfo.event.extendedProps.typeName
            const nextDate =
                eventInfo.event.extendedProps.nextDate != null
                    ? eventInfo.event.extendedProps.nextDate
                    : ''
            const eventTitle = eventInfo.event.title
            const currentDate = new Date()
            const due_date = new Date(nextDate)
            let eventColor
            if (due_date > currentDate) {
                eventColor = 'green'
            } else {
                eventColor =
                    eventInfo.event.extendedProps.type_id === type_id
                        ? 'blue'
                        : 'red'
            }

            const htmlContent = `
            <div style="background-color: ${eventColor}; color:white">
            <div> ${this.$translate('events')}: ${eventTitle}</div>
          <div> ${this.$translate('next_date')}: ${nextDate}</div>
          <div>${this.$translate('customer')}: ${customerName}</div>
          <div>${this.$translate('agent')}: ${agentName}</div>
          <div>${this.$translate('type')}: ${typeName}</div>
        </div>
      `

            return { html: htmlContent }
        },
        updateCustomButton() {
            const calendarApi =
                this.$refs.calendar && this.$refs.calendar.getApi()
            if (!calendarApi) return
            const currentEvents = calendarApi.getEvents()
            const startOfMonth = moment.utc(
                calendarApi.currentData.dateProfile.currentRange.start
            )
            const endDate = moment.utc(
                calendarApi.currentData.dateProfile.currentRange.end
            )
            const formatteStartdDate = moment(
                startOfMonth.toISOString()
            ).format('YYYY-MM-DD')
            const newEndDate = new Date(endDate)
            newEndDate.setDate(newEndDate.getDate() - 1)
            const formattedEndDate = moment(newEndDate.toISOString()).format(
                'YYYY-MM-DD'
            )
            const title = calendarApi.currentData.viewSpec.buttonTextDefault
            const selectedMonthDateTitle = calendarApi.currentData.viewTitle
            this.calendarOptions.buttonText.today = this.getLabel(title)
            const selectedMonthEvents = currentEvents.filter(event => {
                const eventStartDate = moment(event.start.toISOString()).format(
                    'YYYY-MM-DD'
                )
                return (
                    eventStartDate >= formatteStartdDate &&
                    eventStartDate <= formattedEndDate
                )
            })
            const selectedMonthEventsCount = selectedMonthEvents.length
            this.selectedMonthEventsText = `${selectedMonthDateTitle} ${this.$translate(
                'showing'
            )} ${selectedMonthEventsCount} ${this.$translate('events')}`
        },
        getLabel(title) {
            const lowerTitle = title && title.toLowerCase()
            switch (lowerTitle) {
                case this.$translate('month').toLowerCase():
                    return this.$translate('this_month')
                case this.$translate('week').toLowerCase():
                    return this.$translate('this_week')
                case this.$translate('day').toLowerCase():
                    return this.$translate('today')
                case this.$translate('list').toLowerCase():
                    return this.$translate('today')
                default:
                    return this.$translate('this_month')
            }
        },
        async pureSearch() {
            this.loaded = false
            try {
                const { query, filters } = this
                await this.$store.dispatch('assets/fetchSchedule', {
                    query,
                    filters,
                })
                this.calendarOptions.events = this.getAssetSchedulerData(
                    this.assets
                )
            } catch (err) {
                console.error('loading error', err)
            }
        },
    },
}
</script>

  
